// cart-upsells — fetches recommendations from Rebuy's storefront API and
// renders cards in our own markup. We control layout, styling, and refresh
// timing instead of waiting for Rebuy's app embed to hydrate <div data-rebuy-id>.
//
// Fetch: Rebuy recommended-products REST API
//   GET https://rebuyengine.com/api/v1/products/recommended?key=&shopify_product_ids=&limit=&filter_oos=yes
//
// Two contexts, chosen via data-context:
//   drawer  — seeds recommendations from the current cart line items and
//             refetches on every cart update.
//   product — seeds from data-product-ids (the PDP product); static, no
//             cart subscription.
//
// Depends on: pubsub.js (subscribe, publish), constants.js (PUB_SUB_EVENTS)

(function () {
  'use strict';

  // Referenced by both the cart drawer and the cart page section, and only one
  // of those renders per cart_type — but a template could pull in both. Bail on
  // a second execution: customElements.define is already guarded below, the
  // delegated click handlers are not, and double-binding them would add each
  // upsell to the cart twice.
  if (window.__cartUpsellsLoaded) return;
  window.__cartUpsellsLoaded = true;

  var REBUY_RECOMMENDED_URL = 'https://rebuyengine.com/api/v1/products/recommended';
  // Custom-endpoint (ruleset) API — honors the merchandising rules configured
  // in a Rebuy Data Source. {id} is the Data Source / ruleset id, which is a
  // DIFFERENT id from the widget id shown in the theme.
  var REBUY_CUSTOM_URL = 'https://rebuyengine.com/api/v1/custom/id/';
  var FETCH_DEBOUNCE_MS = 250;
  var SKELETON_COUNT = 3;
  var MAX_CARDS = 12;

  // Stale-while-revalidate cache of normalized products, keyed by a signature
  // of context + seed product ids. Lets a re-mounted element (the drawer
  // re-renders on every cart change) paint its last-known cards instantly
  // instead of flashing skeletons through a fresh fetch. Module-level so it
  // survives the element being destroyed and recreated.
  var RENDER_CACHE = new Map();

  function activeCurrency() {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD'
    );
  }
  function formatPrice(value) {
    // Rebuy's recommended-products endpoint returns prices as decimal strings
    // ("8.00", "12.50"). Some other Rebuy endpoints return integer cents (800).
    // Detect dollars by the presence of a decimal point in the source string;
    // otherwise treat 4+ digit integers as cents.
    if (value == null || value === '') return '';
    var str = String(value);
    var hasDecimal = str.indexOf('.') !== -1;
    var n = parseFloat(str);
    if (!isFinite(n)) return '';
    var dollars = hasDecimal ? n : (Number.isInteger(n) && n >= 1000 ? n / 100 : n);
    var hasCents = Math.round(dollars * 100) % 100 !== 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency(),
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(dollars);
    } catch (e) {
      return '$' + dollars.toFixed(hasCents ? 2 : 0);
    }
  }
  function shopifyImageAt(url, width) {
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_' + width + 'x.$1$2');
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c];
    });
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;');
  }

  // Tidy Rebuy titles for the compact carousel: shorten "Plantar Fasciitis
  // Relief" → "PF Relief" and drop a trailing "- Final Run" clearance marker.
  function cleanTitle(title) {
    var t = String(title == null ? '' : title).replace(/Plantar Fasciitis Relief/i, 'PF Relief');
    // Strip clearance/edition markers anywhere in the title (case-insensitive),
    // including a preceding separator, then tidy any leftover separator/space.
    t = t.replace(/\s*[-–—|]?\s*\b(Final Run|Limited Edition)\b/gi, ' ');
    t = t.replace(/\s{2,}/g, ' ').replace(/\s*[-–—|]\s*$/, '').trim();
    return t;
  }

  // Rebuy doesn't give us the sock_type metafield, so the Graduated Compression
  // line is matched by name. It may carry a leading line word ("Active",
  // "Everyday") which is styled with it, matching product-title-listo.liquid.
  var GC_TYPE = /^((?:[a-z]+\s+)?graduated compression)\b/i;

  // Split the family/type prefix from the rest so it can be bolded, mirroring
  // the theme's sock_type logic (product-title-listo.liquid): the first word,
  // extended to include a trailing number so "Merino 10" stays together, or a
  // known two-word family so "Graduated Compression" isn't split mid-name.
  var TWO_WORD_TYPES = [/^graduated$/i];
  function splitTypePrefix(title) {
    var t = cleanTitle(title);
    var gc = t.match(GC_TYPE);
    if (gc) {
      return { type: gc[1], rest: t.slice(gc[1].length).trim() };
    }
    var words = t.split(/\s+/);
    if (!words.length) return { type: '', rest: '' };
    var take = 1;
    if (words[1] && /^\d+$/.test(words[1])) take = 2; // e.g. "Merino 10"
    if (words[1] && TWO_WORD_TYPES.some(function (re) { return re.test(words[0]); })) take = 2;
    return {
      type: words.slice(0, take).join(' '),
      rest: words.slice(take).join(' '),
    };
  }

  // Normalize whatever Rebuy returns. Keep the full variants array so cards
  // can render an option picker when there's more than one selectable variant.
  function normalizeProduct(p) {
    if (!p) return null;
    var variants = Array.isArray(p.variants) ? p.variants : [];
    var firstVariant = variants[0] || {};
    var image =
      p.featured_image ||
      p.image ||
      (p.images && (p.images[0].src || p.images[0])) ||
      '';
    // Resolve a per-variant image: each variant has an image_id, and each
    // product image lists the variant_ids it belongs to. Build both lookups so
    // we can swap the card image to the selected variant.
    var imagesById = {};
    var imageByVariant = {};
    (Array.isArray(p.images) ? p.images : []).forEach(function (img) {
      if (!img) return;
      var src = typeof img === 'string' ? img : img.src;
      if (img.id != null) imagesById[img.id] = src;
      (img.variant_ids || []).forEach(function (vid) { imageByVariant[vid] = src; });
    });
    variants = variants.map(function (v) {
      var vImage = imageByVariant[v.id] || (v.image_id != null ? imagesById[v.image_id] : '') || '';
      return Object.assign({}, v, { _image: vImage });
    });
    var url = p.url || (p.handle ? '/products/' + p.handle : '#');
    var optionName = '';
    if (Array.isArray(p.options) && p.options[0]) {
      optionName = p.options[0].name || (typeof p.options[0] === 'string' ? p.options[0] : '');
    }
    return {
      id: p.id || p.product_id,
      title: p.title || p.name || '',
      url: url,
      image: typeof image === 'string' ? image : image.src || '',
      variantId: firstVariant.id || p.variant_id || p.variants_id,
      price: firstVariant.price != null ? firstVariant.price : p.price,
      comparePrice:
        firstVariant.compare_at_price != null
          ? firstVariant.compare_at_price
          : p.compare_at_price,
      variants: variants,
      optionName: optionName || 'option',
      handle: p.handle,
    };
  }

  // ---------------------------------------------------------------------------
  // <cart-upsells> custom element
  // ---------------------------------------------------------------------------
  class CartUpsells extends HTMLElement {
    constructor() {
      super();
      this._unsub = null;
      this._abortCtrl = null;
      this._refreshTimer = null;
    }

    connectedCallback() {
      this.widgetId = this.dataset.widgetId || this.getAttribute('widget-id') || '';
      this.apiKey = this.dataset.apiKey || this.getAttribute('api-key') || '';
      // Data Source / ruleset id. When set, recommendations come from that
      // curated Rebuy ruleset instead of the generic recommendation engine.
      this.rulesetId = this.dataset.rulesetId || '';
      this.context = this.dataset.context || 'drawer';
      // 'drawer' and 'cart' are both seeded from the live cart and refetch on
      // cart updates; they differ only in what the add-to-cart handler swaps.
      this.isCartContext = this.context === 'drawer' || this.context === 'cart';
      // Layout: 'scroller' (horizontal, arrows + scrollbar — cart drawer) or
      // 'stack' (vertical column — PDP "Pair it with").
      this.layout = this.dataset.layout || (this.context === 'product' ? 'stack' : 'scroller');
      // Max cards shown. The PDP stack is capped low (2) so it doesn't run long
      // down the column; the drawer scroller shows the full set.
      this.maxCards = parseInt(this.dataset.max, 10) || (this.layout === 'stack' ? 2 : MAX_CARDS);
      this.title = this.dataset.title || 'You might also like';
      // Seed product ids: the PDP product, or (server-rendered) the cart's
      // product ids so the drawer skips the extra /cart.js round trip.
      this.seedIds = (this.dataset.productIds || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);

      // Full cart context (server-rendered on the drawer) so the ruleset gets
      // the same input the live Rebuy widget passes — line items, quantities,
      // subtotal, counts. Without it, conditional rules evaluate differently.
      this.cartItems = [];
      if (this.dataset.cartItems) {
        try { this.cartItems = JSON.parse(this.dataset.cartItems) || []; } catch (e) { this.cartItems = []; }
      }
      this.cartSubtotal = this.dataset.cartSubtotal || '';
      this.cartItemCount = this.dataset.cartItemCount || '';
      this.cartLineCount = this.dataset.cartLineCount || '';

      if (!this.apiKey) {
        // No storefront key configured — render nothing rather than a broken
        // widget. (Set it in Theme settings → Rebuy upsells.)
        this.innerHTML = '';
        return;
      }

      // Instant paint from cache when we already know the seed ids (drawer +
      // PDP both server-render them), then revalidate in the background.
      var cached = this.seedIds.length ? RENDER_CACHE.get(this._cacheKey()) : null;
      if (cached && cached.length) {
        this._renderCards(cached);
      } else {
        this._renderSkeleton();
      }

      // Drawer context refetches on cart changes (debounced). Skip
      // attribute-only mutations (/cart/update.js) — line items unchanged, so
      // the recommendations are still valid. Product context is static.
      if (
        this.isCartContext &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          var url = (event && event.url) || '';
          if (/\/cart\/update(\.js)?$/.test(url)) return;
          this._scheduleRefresh();
        });
      }

      this._refresh();
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      if (this._abortCtrl) this._abortCtrl.abort();
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      if (this._scrollPoll) clearInterval(this._scrollPoll);
      if (this._resizeObs) this._resizeObs.disconnect();
    }

    _scheduleRefresh() {
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this._refresh(), FETCH_DEBOUNCE_MS);
    }

    _cacheKey(productIds) {
      var ids = productIds || this.seedIds || [];
      // Include the cart totals so the cached render invalidates when the cart
      // changes quantity/subtotal without the product set itself changing.
      var cartSig = this.isCartContext
        ? '|' + this.cartItemCount + '|' + this.cartSubtotal
        : '';
      return this.context + '|' + this.layout + '|' + ids.slice().sort().join(',') + cartSig;
    }

    async _refresh() {
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();
      var signal = this._abortCtrl.signal;

      try {
        var productIds = await this._sourceProductIds(signal);
        var products = await this._fetchRebuy(signal, productIds);
        if (!products || !products.length) {
          if (!this.querySelector('.cart-upsells__card:not(.cart-upsells__card--skeleton)')) {
            this.innerHTML = '';
          }
          return;
        }
        var normalized = products.map(normalizeProduct).filter(Boolean).slice(0, this.maxCards);
        RENDER_CACHE.set(this._cacheKey(productIds), normalized);
        // _renderCards sets this.innerHTML internally and binds listeners to
        // those elements — call it for its side effect only.
        this._renderCards(normalized);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[cart-upsells] refresh failed; keeping previous render', err);
        }
      }
    }

    async _sourceProductIds(signal) {
      // Use server-rendered seed ids when present (PDP product, or the cart's
      // product ids rendered into the drawer) — skips the /cart.js round trip.
      if (this.seedIds.length) return this.seedIds;
      // Fallback (no seed ids): read the current cart's product ids.
      try {
        var cart = await fetch('/cart.js', { signal }).then((r) => r.json());
        if (!cart || !Array.isArray(cart.items)) return [];
        return cart.items.map(function (i) { return i.product_id; }).filter(Boolean);
      } catch (e) {
        return [];
      }
    }

    async _fetchRebuy(signal, productIds) {
      // Prefer the curated Data Source / ruleset when configured — it returns
      // the merchandising set for this widget rather than the generic engine's
      // guesses. Falls back to the generic recommended-products endpoint.
      var url = this.rulesetId
        ? new URL(REBUY_CUSTOM_URL + encodeURIComponent(this.rulesetId))
        : new URL(REBUY_RECOMMENDED_URL);
      if (this.apiKey) url.searchParams.set('key', this.apiKey);
      if (productIds && productIds.length) {
        url.searchParams.set('shopify_product_ids', productIds.join(','));
      }
      // Pass the full cart context (drawer) so the ruleset matches the live
      // widget: line items with quantities, subtotal, and counts.
      if (this.cartItems && this.cartItems.length) {
        this.cartItems.forEach(function (it, i) {
          if (it.product_id != null) url.searchParams.set('cart[items][' + i + '][product_id]', it.product_id);
          if (it.variant_id != null) url.searchParams.set('cart[items][' + i + '][variant_id]', it.variant_id);
          url.searchParams.set('cart[items][' + i + '][quantity]', it.quantity || 1);
        });
        if (this.cartSubtotal) url.searchParams.set('cart_subtotal', this.cartSubtotal);
        if (this.cartItemCount) url.searchParams.set('cart_item_count', this.cartItemCount);
        if (this.cartLineCount) url.searchParams.set('cart_line_count', this.cartLineCount);
      }
      url.searchParams.set('limit', String(this.maxCards));
      url.searchParams.set('filter_oos', 'yes');
      var requestUrl = url.toString();
      var r = await fetch(requestUrl, { signal, credentials: 'omit' });
      if (!r.ok) {
        var body = await r.text().catch(function () { return '(no body)'; });
        console.warn('[cart-upsells] Rebuy', r.status, requestUrl, '→', body);
        throw new Error('REST ' + r.status);
      }
      var data = await r.json();
      return data.data || data.products || (Array.isArray(data) ? data : []);
    }

    _arrowsHtml() {
      return (
        '<button type="button" class="cart-upsells__arrow cart-upsells__arrow--prev" data-arrow="prev" aria-label="Previous">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M9 2L4 7l5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button type="button" class="cart-upsells__arrow cart-upsells__arrow--next" data-arrow="next" aria-label="Next">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M5 2l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>'
      );
    }

    _renderSkeleton() {
      var skeletons = '';
      var count = Math.min(SKELETON_COUNT, this.maxCards || SKELETON_COUNT);
      for (var i = 0; i < count; i++) {
        skeletons += '<article class="cart-upsells__card cart-upsells__card--skeleton" aria-hidden="true"></article>';
      }
      this.innerHTML = this._wrapperHtml(skeletons);
      this._wireScrollerInputs();
    }

    _renderCards(products) {
      var cards = products.map((p) => this._renderCard(p)).join('');
      this.innerHTML = this._wrapperHtml(cards);
      this.querySelectorAll('[data-variant-select]').forEach(function (sel) {
        sel.setCustomValidity('');
      });
      this._wireScrollerInputs();
    }

    _wrapperHtml(slidesHtml) {
      var labelId = 'cart-upsells-title-' + (this.context || 'x');
      var titleHtml =
        '<h3 class="cart-upsells__title" id="' + labelId + '">' + escapeHtml(this.title) + '</h3>';

      // Stack layout (PDP): a plain vertical column of full-width cards — no
      // horizontal scroller, arrows, or scrollbar; every recommendation shown.
      if (this.layout === 'stack') {
        return (
          '<div class="cart-upsells cart-upsells--stack">' +
            titleHtml +
            '<div class="cart-upsells__list">' + slidesHtml + '</div>' +
          '</div>'
        );
      }

      // Scroller layout (cart drawer): horizontal, arrows on hover + scrollbar.
      return (
        '<div class="cart-upsells cart-upsells--scroller">' +
          titleHtml +
          '<div class="cart-upsells__scroller"' +
            ' role="region"' +
            ' aria-labelledby="' + labelId + '"' +
            ' tabindex="0">' +
            slidesHtml +
          '</div>' +
          this._arrowsHtml() +
          '<div class="cart-upsells__bar"' +
            ' role="scrollbar"' +
            ' aria-orientation="horizontal"' +
            ' aria-valuemin="0"' +
            ' aria-valuemax="100"' +
            ' aria-valuenow="0"' +
            ' aria-controls="' + labelId + '">' +
            '<div class="cart-upsells__bar-thumb"></div>' +
          '</div>' +
        '</div>'
      );
    }

    _wireScrollerInputs() {
      // Stack layout has no scroller to wire.
      if (this.layout === 'stack') return;
      if (this._scrollPoll) clearInterval(this._scrollPoll);

      var scroller = this.querySelector('.cart-upsells__scroller');
      var thumb = this.querySelector('.cart-upsells__bar-thumb');
      var bar = this.querySelector('[role="scrollbar"]');
      if (!scroller) return;
      var self = this;

      var prevArrow = this.querySelector('[data-arrow="prev"]');
      var nextArrow = this.querySelector('[data-arrow="next"]');

      var updateThumb = function () {
        var sw = scroller.scrollWidth;
        var cw = scroller.clientWidth;
        // Not laid out yet (0 width) — leave the thumb as-is and let the next
        // poll/observer tick recompute once the scroller has real dimensions.
        if (cw <= 0) return;
        var max = sw - cw;
        var progressPct = 0;
        // Hide the whole bar when there's nothing to scroll, rather than show a
        // full-width thumb that reads as broken.
        if (bar) bar.style.display = max <= 1 ? 'none' : '';
        if (thumb) {
          if (max <= 1) {
            thumb.style.width = '100%';
            thumb.style.left = '0%';
          } else {
            var visible = cw / sw;
            var progress = scroller.scrollLeft / max;
            progressPct = Math.round(progress * 100);
            thumb.style.width = (visible * 100) + '%';
            thumb.style.left = (progress * (1 - visible) * 100) + '%';
          }
        }
        if (bar) bar.setAttribute('aria-valuenow', String(progressPct));
        if (prevArrow) {
          prevArrow.classList.toggle('is-disabled', scroller.scrollLeft <= 1);
          prevArrow.setAttribute('aria-disabled', String(scroller.scrollLeft <= 1));
        }
        if (nextArrow) {
          nextArrow.classList.toggle('is-disabled', scroller.scrollLeft >= max - 1);
          nextArrow.setAttribute('aria-disabled', String(scroller.scrollLeft >= max - 1));
        }
      };

      // Drag the bar to scroll — delta-based scrubbing, 1:1 thumb-follows-cursor.
      if (bar) {
        bar.addEventListener('pointerdown', function (evt) {
          evt.preventDefault();
          evt.stopPropagation();
          bar.setPointerCapture && bar.setPointerCapture(evt.pointerId);
          bar.classList.add('is-dragging');

          var rect = bar.getBoundingClientRect();
          var sw = scroller.scrollWidth;
          var cw = scroller.clientWidth;
          var max = sw - cw;
          var visible = cw / sw;
          var ratio = max <= 0 || rect.width <= 0
            ? 0
            : max / (Math.max(0.0001, 1 - visible) * rect.width);

          var startX = evt.clientX;
          var startScroll = scroller.scrollLeft;

          var onMove = function (em) {
            var dx = em.clientX - startX;
            scroller.scrollLeft = Math.max(0, Math.min(max, startScroll + dx * ratio));
          };
          var onUp = function () {
            bar.classList.remove('is-dragging');
            bar.removeEventListener('pointermove', onMove);
            bar.removeEventListener('pointerup', onUp);
            bar.removeEventListener('pointercancel', onUp);
          };
          bar.addEventListener('pointermove', onMove);
          bar.addEventListener('pointerup', onUp);
          bar.addEventListener('pointercancel', onUp);
        });
      }

      scroller.addEventListener('keydown', function (evt) {
        if (evt.key === 'ArrowRight') {
          evt.preventDefault();
          scrollByCard(1);
        } else if (evt.key === 'ArrowLeft') {
          evt.preventDefault();
          scrollByCard(-1);
        } else if (evt.key === 'Home') {
          evt.preventDefault();
          scroller.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (evt.key === 'End') {
          evt.preventDefault();
          scroller.scrollTo({ left: scroller.scrollWidth, behavior: 'smooth' });
        }
      });

      var scrollByCard = function (direction, evt) {
        if (evt) {
          evt.preventDefault();
          evt.stopPropagation();
        }
        var firstCard = scroller.querySelector('.cart-upsells__card');
        var step = firstCard && firstCard.offsetWidth > 0
          ? firstCard.offsetWidth + 10
          : Math.max(200, scroller.clientWidth * 0.8);
        var max = scroller.scrollWidth - scroller.clientWidth;
        var target = Math.max(0, Math.min(max, scroller.scrollLeft + direction * step));
        try {
          scroller.scrollTo({ left: target, behavior: 'smooth' });
        } catch (e) {
          scroller.scrollLeft = target;
        }
      };
      var bindArrow = function (arrow, dir) {
        if (!arrow) return;
        var fire = function (evt) {
          if (arrow.classList.contains('is-disabled')) return;
          scrollByCard(dir, evt);
        };
        arrow.addEventListener('click', fire);
        arrow.addEventListener('pointerdown', function (evt) {
          evt.stopPropagation();
        });
      };
      bindArrow(prevArrow, -1);
      bindArrow(nextArrow, 1);
      scroller.addEventListener('scroll', updateThumb, { passive: true });
      window.addEventListener('resize', updateThumb);
      // The drawer opens via a visibility/transform toggle, so the scroller has
      // no measurable width until it's shown. A ResizeObserver recomputes the
      // thumb the moment the scroller gains real dimensions (drawer opened),
      // which the one-shot calls below would otherwise miss.
      if (this._resizeObs) this._resizeObs.disconnect();
      if (window.ResizeObserver) {
        this._resizeObs = new ResizeObserver(function () { updateThumb(); });
        this._resizeObs.observe(scroller);
      }
      requestAnimationFrame(updateThumb);
      updateThumb();
      setTimeout(updateThumb, 200);
      // A few staggered recomputes catch late layout (fonts/images) without a
      // persistent interval — the old 100ms poll wrote thumb.style every tick,
      // which hammered the cart drawer's subtree MutationObserver.
      setTimeout(updateThumb, 600);
      setTimeout(updateThumb, 1200);

      // Vertical wheel → horizontal scroll.
      scroller.addEventListener('wheel', function (evt) {
        var dy = evt.deltaY;
        if (Math.abs(dy) <= Math.abs(evt.deltaX)) return;
        var max = scroller.scrollWidth - scroller.clientWidth;
        if (max <= 0) return;
        var atLeftEdge = scroller.scrollLeft <= 0 && dy < 0;
        var atRightEdge = scroller.scrollLeft >= max && dy > 0;
        if (atLeftEdge || atRightEdge) return;
        evt.preventDefault();
        scroller.scrollLeft += dy;
      }, { passive: false });

      // Click-and-drag horizontal scrolling.
      var dragging = false;
      var dragStartX = 0;
      var dragStartScroll = 0;
      var moved = false;
      scroller.addEventListener('mousedown', function (evt) {
        // Don't start a drag from interactive elements — including the card's
        // image/title links, so a normal click navigates to the PDP instead of
        // being swallowed by the drag/click-suppression below.
        if (evt.target.closest('a, button, select, input')) return;
        dragging = true;
        moved = false;
        dragStartX = evt.pageX;
        dragStartScroll = scroller.scrollLeft;
        scroller.classList.add('is-dragging');
      });
      window.addEventListener('mousemove', function (evt) {
        if (!dragging) return;
        var dx = evt.pageX - dragStartX;
        if (Math.abs(dx) > 3) moved = true;
        scroller.scrollLeft = dragStartScroll - dx;
      });
      window.addEventListener('mouseup', function () {
        if (!dragging) return;
        dragging = false;
        scroller.classList.remove('is-dragging');
      });
      scroller.addEventListener('click', function (evt) {
        // Never suppress a click on a link/button — those must always fire
        // (navigate to the PDP / add to cart).
        if (evt.target.closest('a, button, select')) return;
        if (moved) {
          evt.preventDefault();
          evt.stopPropagation();
          moved = false;
        }
      }, true);
    }

    _renderCard(p) {
      var firstAvailable =
        (p.variants && p.variants.find(function (v) { return v.available !== false; })) ||
        (p.variants && p.variants[0]) || null;
      var hasMultipleVariants = p.variants && p.variants.length > 1;

      var variantLabel = '';
      if (
        !hasMultipleVariants &&
        firstAvailable &&
        firstAvailable.title &&
        firstAvailable.title !== 'Default Title'
      ) {
        variantLabel =
          '<span class="cart-upsells__card-variant" data-variant-label>' +
            escapeHtml(firstAvailable.title) +
          '</span>';
      }

      var displayPrice = p.price;
      if (hasMultipleVariants) {
        var prices = p.variants
          .map(function (v) { return parseFloat(v.price); })
          .filter(function (n) { return isFinite(n); });
        if (prices.length) displayPrice = Math.min.apply(null, prices);
      }

      var priceHtml = '';
      if (p.comparePrice && Number(p.comparePrice) > Number(displayPrice)) {
        priceHtml =
          '<span class="cart-upsells__card-price">' + formatPrice(displayPrice) + '</span>' +
          '<span class="cart-upsells__card-compare">' + formatPrice(p.comparePrice) + '</span>';
      } else if (displayPrice != null) {
        priceHtml =
          '<span class="cart-upsells__card-price">' + formatPrice(displayPrice) + '</span>';
      }
      priceHtml = variantLabel + priceHtml;

      var ctaHtml = '';
      if (p.variants && p.variants.length > 1) {
        var firstAvail = p.variants.find(function (v) { return v.available !== false; }) || p.variants[0];
        var optionsHtml = p.variants.map(function (v) {
          var disabled = v.available === false ? ' disabled' : '';
          var selected = v.id === firstAvail.id ? ' selected' : '';
          var label = v.title || (Array.isArray(v.options) ? v.options.join(' / ') : '');
          return '<option value="' + escapeAttr(v.id) +
            '" data-price="' + escapeAttr(v.price) + '"' +
            ' data-variant-image="' + escapeAttr(v._image || '') + '"' + selected + disabled + '>' +
            escapeHtml(label) + ' — ' + formatPrice(v.price) +
            '</option>';
        }).join('');
        var optionAriaLabel = 'Choose ' + (p.optionName || 'option') + ' for ' + p.title;
        ctaHtml =
          '<button type="button" class="cart-upsells__card-cta" data-toggle-options' +
            ' aria-expanded="false" aria-controls="upsell-options-' + escapeAttr(p.id || '') + '">' +
            'Choose ' + escapeHtml(p.optionName || 'option') +
          '</button>' +
          '<div class="cart-upsells__card-options"' +
            ' id="upsell-options-' + escapeAttr(p.id || '') + '"' +
            ' aria-hidden="true">' +
            '<select class="cart-upsells__card-select" data-variant-select' +
              ' aria-label="' + escapeAttr(optionAriaLabel) + '">' + optionsHtml + '</select>' +
            '<button type="button" class="cart-upsells__card-cta cart-upsells__card-cta--confirm"' +
              ' data-variant-id="' + escapeAttr(firstAvail.id) + '"' +
              ' data-product-title="' + escapeAttr(p.title) + '"' +
              ' aria-label="Add ' + escapeAttr(p.title) + ' to cart">' +
              'Add' +
            '</button>' +
          '</div>';
      } else if (p.variantId) {
        ctaHtml = '<button type="button" class="cart-upsells__card-cta"' +
          ' data-variant-id="' + escapeAttr(p.variantId) + '"' +
          ' data-product-title="' + escapeAttr(p.title) + '">Add</button>';
      }

      // Title: bold the family/type prefix (ELITE, MERINO 10) and clean the
      // "- Final Run" marker, mirroring the theme's product-title logic.
      var parts = splitTypePrefix(p.title);
      var titleHtml =
        (parts.type
          ? '<span class="cart-upsells__card-type">' + escapeHtml(parts.type) + '</span>'
          : '') +
        (parts.rest
          ? ' <span class="cart-upsells__card-name">' + escapeHtml(parts.rest) + '</span>'
          : '');
      var cleanAlt = cleanTitle(p.title);
      // Show the selected variant's image on load (fall back to the product image).
      var cardImage = (firstAvailable && firstAvailable._image) || p.image;

      return (
        '<article class="cart-upsells__card" data-product-id="' + escapeAttr(p.id || '') + '">' +
          '<a href="' + escapeAttr(p.url) + '" class="cart-upsells__card-link" tabindex="-1">' +
            (cardImage
              ? '<img class="cart-upsells__card-image" src="' + escapeAttr(shopifyImageAt(cardImage, 300)) +
                '" alt="' + escapeAttr(cleanAlt) + '" loading="lazy" decoding="async">'
              : '<span class="cart-upsells__card-image cart-upsells__card-image--empty"></span>') +
          '</a>' +
          '<div class="cart-upsells__card-body">' +
            '<p class="cart-upsells__card-title">' +
              '<a href="' + escapeAttr(p.url) + '">' + titleHtml + '</a>' +
            '</p>' +
            '<div class="cart-upsells__card-pricing">' + priceHtml + '</div>' +
            ctaHtml +
          '</div>' +
        '</article>'
      );
    }
  }

  if (!customElements.get('cart-upsells')) {
    customElements.define('cart-upsells', CartUpsells);
  }

  // ---------------------------------------------------------------------------
  // Variant picker — clicking "Choose [option]" reveals a <select> + confirm.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (evt) {
    var toggle = evt.target.closest('[data-toggle-options]');
    if (!toggle) return;
    evt.preventDefault();
    evt.stopPropagation();
    var card = toggle.closest('.cart-upsells__card');
    if (!card) return;
    // Opening one picker reveals every card's picker in the same widget, so the
    // whole row is in the same "choose a variant" state at once.
    var container = toggle.closest('cart-upsells') || toggle.closest('.cart-upsells');
    var scope = container || card;
    scope.querySelectorAll('.cart-upsells__card-options').forEach(function (panel) {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
    });
    scope.querySelectorAll('[data-toggle-options]').forEach(function (tog) {
      tog.setAttribute('aria-expanded', 'true');
      tog.hidden = true;
    });
    var sel = card.querySelector('[data-variant-select]');
    if (sel) sel.focus();
  });

  // Capture phase + stopPropagation: the variant <select> lives inside the
  // cart-drawer's form, and <cart-drawer-items> runs quantity validation on any
  // change event in that subtree. Stop the event before it bubbles so cart.js
  // never tries to read step/min/max off our select.
  document.addEventListener('change', function (evt) {
    var sel = evt.target.closest('[data-variant-select]');
    if (!sel) return;
    evt.stopPropagation();
    sel.setCustomValidity('');
    var card = sel.closest('.cart-upsells__card');
    if (!card) return;
    var confirm = card.querySelector('.cart-upsells__card-cta--confirm');
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    if (confirm) {
      confirm.dataset.variantId = sel.value;
      confirm.textContent = 'Add';
    }
    var label = card.querySelector('[data-variant-label]');
    if (label) {
      var optText = (opt.textContent || '').split('—')[0].trim();
      if (optText) label.textContent = optText;
    }
    var priceEl = card.querySelector('.cart-upsells__card-price');
    if (priceEl && opt.dataset.price != null) {
      priceEl.textContent = formatPrice(opt.dataset.price);
    }
    var prefix = card.querySelector('[data-price-prefix]');
    if (prefix) prefix.remove();
    // Swap the card image to the selected variant's image.
    var img = card.querySelector('img.cart-upsells__card-image');
    if (img && opt.dataset.variantImage) {
      img.src = shopifyImageAt(opt.dataset.variantImage, 300);
    }
  }, true);

  document.addEventListener('input', function (evt) {
    if (evt.target.closest('[data-variant-select]')) evt.stopPropagation();
  }, true);

  // ---------------------------------------------------------------------------
  // Add-to-cart handler — delegated, refreshes the drawer via Section Rendering.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', async function (evt) {
    var btn = evt.target.closest('.cart-upsells__card-cta[data-variant-id]');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    /*
     * Attribution. Underscore-prefixed line item properties are hidden from the
     * customer in cart and checkout but persist onto the order, so upsell-driven
     * revenue can be separated from the rest by surface. The handler is delegated
     * from the document, so the surface is read off the originating element rather
     * than instance state.
     */
    var host = btn.closest('cart-upsells');
    var context = (host && host.dataset.context) || 'drawer';
    var ATTRIBUTION = { product: 'PDP rebuy', cart: 'Cart Page rebuy', drawer: 'Cart Drawer rebuy' };
    var attribution = ATTRIBUTION[context] || ATTRIBUTION.drawer;
    var onCartPage = context === 'cart';

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_source]', 'Rebuy');
      formData.append('properties[_attribution]', attribution);
      // Ask for the cart-drawer + cart count HTML back so we can swap in place.
      // The cart page renders its own line items, so refresh that section
      // instead of the drawer the shopper cannot see.
      formData.append('sections', onCartPage ? 'main-cart-items-listo,cart-icon-count' : 'cart-drawer,cart-icon-count');
      formData.append('sections_url', window.location.pathname);

      var r = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!r.ok) throw new Error('add failed: ' + r.status);
      var data = await r.json();

      if (data.sections && typeof data.sections === 'object') {
        Object.keys(data.sections).forEach(function (sectionId) {
          var html = data.sections[sectionId];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (sectionId === 'cart-drawer') {
            var fresh = doc.querySelector('.cart-drawer-listo__inner');
            var live = document.querySelector('cart-drawer .cart-drawer-listo__inner');
            if (fresh && live) live.innerHTML = fresh.innerHTML;
          } else if (sectionId === 'main-cart-items-listo') {
            // Same selector cart.js swaps on a quantity change.
            var freshCart = doc.querySelector('.cart-page-listo__container');
            var liveCart = document.querySelector('.cart-page-listo__container');
            if (freshCart && liveCart) liveCart.innerHTML = freshCart.innerHTML;
          } else if (sectionId === 'cart-icon-count') {
            // Feetures renders the count bubble in more than one place
            // (desktop + mobile header); update every live instance.
            var freshCount = doc.querySelector('#cart-icon-count') || doc.body;
            document.querySelectorAll('#cart-icon-count').forEach(function (liveCount) {
              liveCount.innerHTML = freshCount.innerHTML;
            });
          }
        });
      }

      // Notify other listeners (cart count, our own drawer subscription).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsells', variantId: variantId });
      }

      // Open the drawer if it's not already. Never on the cart page — the
      // shopper is already looking at the cart.
      if (onCartPage) return;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch (err) {
      console.warn('[cart-upsells] add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  });
})();
